import { useRef } from 'react';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { TourKeys } from '@cast/constants';
import { AuditLogEntry, AuditLogFilter, AuditLogListEntry } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { useIsTourActive } from 'core/tour-v2/hooks/useIsTourActive';

const AUDIT_QUERY_KEY = 'audit';

const getAuditLogQueryKey = (filter?: unknown) =>
  [AUDIT_QUERY_KEY, filter] as const;

type UseGetAuditLogQuery = {
  isLoading: boolean;
  isFetching: boolean;
  auditLog?: (AuditLogEntry | AuditLogListEntry)[];
  error: unknown;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  refetch?: () => void;
};

type AuditLogQueryArgs = {
  filter: AuditLogFilter | undefined;
  tag?: number;
  refetchInterval?: (data: unknown) => number | false;
  enabled?: boolean;
  forceOldFetch?: boolean;
  meta?: Record<string, unknown>;
};

export const useAuditLogQuery = ({
  filter,
  tag,
  refetchInterval,
  enabled,
  forceOldFetch,
  meta,
}: AuditLogQueryArgs): UseGetAuditLogQuery => {
  const client = useQueryClient();
  const isTourActive = useIsTourActive(TourKeys.OpinionatedPlatform);

  // Store tag in ref so queryFn always has latest value when called via refetchQueries()
  const tagRef = useRef(tag);
  tagRef.current = tag;

  const {
    isLoading,
    isFetching,
    data: auditLogResponse,
    error,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: getAuditLogQueryKey(filter),
    queryFn: async ({ pageParam }) => {
      if (!isTourActive && !forceOldFetch) {
        const { data } = await apiClient.auditLog.getAuditListEvent({
          ...(pageParam ? { 'page.cursor': pageParam } : {}),
          ...filter,
        });
        return data || {};
      }
      const { data } = await apiClient.auditLog.getAuditLog(
        {
          ...(pageParam ? { 'page.cursor': pageParam } : {}),
          ...filter,
        },
        { middlewareArgs: { requestMeta: { tag: tagRef.current } } }
      );

      return data || {};
    },
    enabled: enabled && !!filter,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: ({ queryKey }) => {
      if (refetchInterval) {
        return refetchInterval(client.getQueryData(queryKey));
      }
      return false;
    },
    meta,
  });

  return {
    isLoading,
    isFetching,
    auditLog: flatMap(auditLogResponse?.pages, (page) =>
      (page.items ?? []).map((i) => i)
    ),
    error,
    fetchNextPage,
    hasNextPage,
    refetch,
  };
};
